// src/hooks/useReportIntake.js
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../supabaseClient"; // adjust if your path differs

/** Get current academic label in Asia/Kuala_Lumpur as YY/YY (e.g., 25/26). */
export function getAcademicLabel(date = new Date()) {
  const month = Number(
    new Intl.DateTimeFormat("en-US", {
      month: "numeric",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(date)
  );
  const fullYear = Number(
    new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(date)
  );

  const yy = fullYear % 100;
  const label =
    month >= 7
      ? `${String(yy).padStart(2, "0")}/${String((yy + 1) % 100).padStart(2, "0")}`
      : `${String((yy - 1 + 100) % 100).padStart(2, "0")}/${String(yy).padStart(2, "0")}`;

  return label;
}

/** Fetch intakes list */
export function useIntakes() {
  return useQuery({
    queryKey: ["dashboard_intakes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("intakes")
        .select("id, name, start_date, end_date")
        .order("start_date", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

/** Pick default intake id by matching name to current YY/YY label */
export function useDefaultIntakeId(intakes) {
  const label = getAcademicLabel();
  return useMemo(() => {
    if (!Array.isArray(intakes)) return null;
    const found = intakes.find((i) => i?.name === label);
    return found ? found.id : null;
  }, [intakes, label]);
}

/** Fetch subjects (for selector and matrix columns) */
export function useSubjects() {
  return useQuery({
    queryKey: ["dashboard_subjects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subjects")
        .select("id, code, name")
        .order("code", { ascending: true });
      if (error) throw error;
      return (data ?? []).filter((s) => s.code && s.name);
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

/** Aggregate enrollment rows into per-student matrix rows */
export function aggregateEnrollmentsToMatrix({ rows, subjectCodes }) {
  const byStudent = new Map();
  const codes = [...subjectCodes].sort();

  for (const r of rows) {
    if (!r?.students || r?.students?.is_deleted) continue;

    const sid = r.students.id;
    if (!byStudent.has(sid)) {
      const baseFlags = Object.fromEntries(codes.map((c) => [c, false]));
      byStudent.set(sid, {
        id: sid,
        full_name: r.students.full_name || "-",
        nric_passport: r.students.nric_passport || "-",
        email: r.students.email || "-",
        countInIntake: 0,
        subjectFlags: { ...baseFlags },
      });
    }
    const entry = byStudent.get(sid);
    entry.countInIntake += 1;
    const code = r.subjects?.code;
    if (code && code in entry.subjectFlags) {
      entry.subjectFlags[code] = true;
    }
  }

  return Array.from(byStudent.values()).sort((a, b) =>
    a.full_name.localeCompare(b.full_name, undefined, { sensitivity: "base" })
  );
}

/** Matrix data (Subject = All): enrollments for intake, join students & subjects */
export function useMatrixData(intakeId) {
  return useQuery({
    enabled: Boolean(intakeId),
    queryKey: ["dashboard_matrix", { intakeId }],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("enrollments")
        .select(
          `
          id,
          is_deleted,
          student_id,
          intake_id,
          students:students (
            id,
            full_name,
            email,
            nric_passport,
            is_deleted
          ),
          subjects:subjects (
            id,
            code
          )
        `
        )
        .eq("intake_id", intakeId)
        .eq("is_deleted", false);
      if (error) throw error;
      return data ?? [];
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

/**
 * Single-subject data (Subject ≠ All): FILTER BY subject_id
 * Page resolves selectedSubjectCode -> subjectId and passes that here.
 */
export function useSingleSubjectData(intakeId, subjectId) {
  return useQuery({
    enabled: Boolean(intakeId) && Boolean(subjectId),
    queryKey: ["dashboard_single", { intakeId, subjectId }],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("enrollments")
        .select(
          `
          id,
          is_deleted,
          student_id,
          intake_id,
          students:students (
            id,
            full_name,
            email,
            nric_passport,
            is_deleted
          )
        `
        )
        .eq("intake_id", intakeId)
        .eq("is_deleted", false)
        .eq("subject_id", subjectId);

      if (error) throw error;

      const rows = (data ?? []).filter(
        (r) => r?.students && !r.students.is_deleted
      );

      const byStudent = new Map();
      for (const r of rows) {
        const s = r.students;
        if (!byStudent.has(s.id)) {
          byStudent.set(s.id, {
            id: s.id,
            full_name: s.full_name || "-",
            email: s.email || "-",
            nric_passport: s.nric_passport || "-",
          });
        }
      }

      return Array.from(byStudent.values()).sort((a, b) =>
        a.full_name.localeCompare(b.full_name, undefined, { sensitivity: "base" })
      );
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}
